import { TradingCategories } from '../../components/pages/TradingPage/TradingPage';
import { OwnedOffchainToken } from '../../replicant/features/game/player.schema';
import { TradingTokenSearchField } from '../../replicant/features/offchainTrading/offchainTrading.asyncGetters';
import {
  CanBuyOpts,
  CanSellOpts,
  computePortfolioValue,
  getBuyTxEstimate,
  getCanUserSell,
  getCoinAmountForTokenSell,
  getCurvePrice,
  getIsBuyTxValid,
  getIsSellTxValid,
  getMyOffchainTokenIds,
  getOffchainTokenListing,
  getTxWithinEstimate,
  TradingToken,
} from '../../replicant/features/offchainTrading/offchainTrading.getters';
import { TradingSearchResult } from '../../replicant/features/offchainTrading/offchainTrading.properties';
import {
  offchainTokenSearchCount,
  offchainTradingNotifDelay,
  REPLICANT_UPLOAD_LENGTH_LIMIT,
  SliceConfig,
  txConfig,
} from '../../replicant/features/offchainTrading/offchainTrading.ruleset';
import {
  TradingTokenHolder,
  TradingTokenInput,
  TradingTokenListing,
  TxType,
} from '../../replicant/features/offchainTrading/types';
import { ErrorCode } from '../../replicant/response';
import { MIN_IN_MS, SEC_IN_MS } from '../../replicant/utils/time';
import { AppController, AppEvents, isLocal } from '../AppController';
import { EventListener } from '../EventListener';
import { Form } from '../Form';
import { captureGenericError } from '../sentry';
import { Optional } from '../types';
import { getPct, waitFor } from '../utils';
import { v4 as uuid } from 'uuid';
import Big from 'big.js';
import { analytics } from '@play-co/gcinstant';
import { shareDeeplink } from '../sharing';
import { delay } from '../../replicant/lib/async';
import { TransactionSuccess } from './UIController/UIController';
import { Tutorials } from '../../data/tutorial/types';
import {
  getOffchainTokenHoldCount,
  getOffchainTokenHoldingStats,
  getOffchainTokenPricePoints,
  getPortfolioPricePoints,
  getPortfolioRoi,
} from '../offchainTokenUtils';

const searchFields: Record<TradingCategories, TradingTokenSearchField> = {
  MyCards: 'supply',
  Hot: 'hotTxsCount',
  Top: 'supply',
  New: 'availableAt',
};

type TradingTokenListingCache = {
  listing: TradingTokenListing[];
  lastOffchainTokenSortFieldValue: number;
  lastOffchainTokenAvailabilityTime: number;
  isEndOfList: boolean;
};

interface PaginatorOpts {
  offchainTokens: TradingSearchResult[];
  searchField: TradingTokenSearchField;
  currentPaginator?: TradingTokenListingCache;
  isRefresh: boolean;
}

const getNextPaginator = ({
  offchainTokens,
  searchField,
  currentPaginator,
  isRefresh,
}: PaginatorOpts) => {
  if (isRefresh && currentPaginator) {
    return currentPaginator;
  }
  // Default
  if (offchainTokens.length < offchainTokenSearchCount) {
    return {
      lastOffchainTokenSortFieldValue: -1,
      lastOffchainTokenAvailabilityTime: -1,
    };
  }
  const lastToken = offchainTokens[offchainTokens.length - 1];

  return {
    lastOffchainTokenSortFieldValue: lastToken[searchField],
    lastOffchainTokenAvailabilityTime: lastToken.availableAt,
  };

  // For the supply field we actually want market cap
  // if (searchField === 'supply') {
  //   paginator = getMarketCap(paginator);
  // }

  // There's always the chance the lastToken has the same value as the previous paginator
  // So we send the same props and get the same response thus getting stuck in a loop
  // const isLoop = Boolean(currentPaginator && currentPaginator === paginator);
  // cai('getNextPaginator-1', {
  //   paginator,
  //   isLoop,
  // });
  // if (isLoop) {
  //   paginator -= curveConfig.microModifier;
  // }

  // cai('getNextPaginator-2', {
  //   paginator
  // });

  // return paginator;
};

interface Tx {
  txType: TxType;
  receive: Big;
  send: Big;
  isValid: boolean;
}

export enum TradingEvents {
  OnUpdate = 'OnUpdate',
  // When are are updating the tx, i.e mode | amount | etc
  OnTxUpdate = 'OnTxUpdate',
  // When we are opening a offchainToken specific view
  OnSelectedOffchainTokenUpdate = 'OnSelectedOffchainTokenUpdate',
  // When the values of
  OnListingUpdate = 'OnListingUpdate',
  // When a new notificaiton is received
  OnNotification = 'OnNotification',
  // To update the timer until we refresh the selected offchainToken data (when on offchainToken details page)
  OnRefreshTimeUpdate = 'OnRefreshTimeUpdate',
  // Whenever there's a change in portfolio
  OnPortfolioUpdate = 'OnPortfolioUpdate',
}

// @warning-start: do not change the prefix or it can break moderation
const OFFCHAIN_TRADING_PREFIX = 'offchainTrading';
const S3_PATH_PREFIX = 'trading';
// @warning-end

const CARD_REFETCH_COOLDOWN_MS = 15;
const LIST_FETCH_COOLDOWN = SEC_IN_MS * 1000;
const PORTFOLIO_REFETCH_COOLDOWN = MIN_IN_MS * 2; // @TODO: Check how often it should be
const DEFAULT_CATEGORY = 'Hot' as TradingCategories;
const NEW_CATEGORY_REFRESH_COOLDOWN = MIN_IN_MS * 1;
const TOP_CATEGORY_REFRESH_COOLDOWN = MIN_IN_MS * 5;

const TRADING_TUTORIAL_ENABLED = true;

export class TradingController extends EventListener {
  // Check if repli
  // Record<UserId, ImageUrl | null>; null if we have attempted to find a phone but there's none

  private notifScheduler: NodeJS.Timeout | null = null;

  private listingByCategory: Record<
    TradingCategories,
    Optional<TradingTokenListingCache>
  > = {
    MyCards: undefined,
    Hot: undefined,
    Top: undefined,
    New: undefined,
  };

  get listingCategory() {
    return this.state.listingCategory;
  }

  // get offchainTokens() {
  //   return this.state.offchainTokens;
  // }

  get notification() {
    return this.state.notification;
  }

  get extendSearch() {
    return this.state.extendSearch;
  }

  get searchTerm() {
    return this.state.searchTerm;
  }

  get isLoading() {
    return this.state.loading;
  }

  get purchaseMode() {
    return this.state.purchaseMode;
  }

  get isBuy() {
    return this.purchaseMode === 'buy';
  }

  get createOffchainTokenForm() {
    return this.state.createOffchainTokenForm;
  }

  get portfolioValue() {
    return this.state.portfolioValue;
  }

  get currentOffchainToken() {
    if (!this.state.selectedOffchainToken) {
      return undefined;
    }
    return {
      ...this.state.selectedOffchainToken,
      curvePrice: getCurvePrice(Big(this.state.selectedOffchainToken.supply)),
      transactions: this.state.selectedOffchainToken.transactions.sort(
        (a, b) => b.createdAt - a.createdAt,
      ),
    };
  }

  get myOffchainToken() {
    if (!this.currentOffchainToken) {
      return undefined;
    }
    return this.app.state.trading.offchainTokens[this.currentOffchainToken.id];
  }

  get myOffchainTokensCount() {
    return Object.keys(this.app.state.trading.offchainTokens).length;
  }

  get listing() {
    const cachedListing = this.listingByCategory[this.listingCategory];
    return {
      items: cachedListing?.listing || [],
      placeholderCount: cachedListing?.isEndOfList ? 0 : 5,
    };
  }

  get isOwner() {
    return this.state.selectedOffchainToken?.creatorId === this.app.state.id;
  }

  get tx() {
    if (
      (!this.state.createOffchainTokenForm &&
        !this.state.selectedOffchainToken) ||
      !this.state.tx
    ) {
      return undefined;
    }

    const canSell = this.state.selectedOffchainToken
      ? getCanUserSell(
          this.app.state,
          this.state.selectedOffchainToken.id,
          Big(0),
        )
      : false;

    return {
      ...this.state.tx,
      maxSlippage: this.app.state.trading.maxSlippage,
      canSell,
    };
  }

  get txSummary() {
    if (!this.state.tx) {
      return;
    }
    const {
      tx: { txType, receive, send },
      selectedOffchainToken,
    } = this.state;

    // default to 'sell'
    let tokenAmount = send;
    let finalSupply = selectedOffchainToken
      ? Big(selectedOffchainToken.supply)
      : undefined;

    if (txType === 'buy') {
      tokenAmount = receive;
      finalSupply = selectedOffchainToken
        ? Big(selectedOffchainToken.supply).add(tokenAmount)
        : undefined;
    }

    return {
      ...this.state.tx,
      shareOfTheSupply: finalSupply
        ? getPct(tokenAmount.toNumber(), finalSupply.toNumber())
        : '-',
      sending: send.toNumber(),
      receiving: receive.toNumber(),
    };
  }

  get timeUntilNextOffchainTokenRefresh() {
    return CARD_REFETCH_COOLDOWN_MS - this.offchainTokenRefreshCount;
  }

  get createStarPrice() {
    const iapConfig = this.app.session.getIAPConfig();
    return iapConfig?.priceInStars;
  }

  get isActive() {
    return true;
  }

  // @TODO: LOCALIZE!!!
  public newTokenForm = new Form({
    image: {
      title: '',
      placeholder: '',
      required: true,
      dataOnly: true,
      validate: (str: string) => {
        const file = new File([str], 'filename');
        // @todo: is it possible to increase this limit?
        return file.size <= REPLICANT_UPLOAD_LENGTH_LIMIT;
      },
    },
    name: {
      title: 'Token Name',
      placeholder: 'Best token name ever',
      validate: (str: string) => str.length > 0,
      required: true,
    },
    ticker: {
      title: 'Ticker',
      placeholder: 'BTC',
      // limit max 8 chars
      transformInput: (value: string) => value.toUpperCase().substring(0, 8),
      validate: (str: string) => str.length >= 3 && str.length <= 8,
      required: true,
    },
    description: {
      title: 'Description',
      placeholder: 'The coolest of them all',
      validate: (str: string) => str.length > 0,
      required: true,
    },
    telegramChannelLink: {
      title: 'Telegram channel link',
      placeholder: 'Paste Link',
      validate: (str: string) =>
        str.length === 0 || str.startsWith('https://t.me/'),
      required: false,
    },
    telegramChatLink: {
      title: 'Telegram chat link',
      placeholder: 'Paste Link',
      validate: (str: string) =>
        str.length === 0 || str.startsWith('https://t.me/joinchat/'),
      required: false,
    },
    twitterLink: {
      title: 'Twitter link',
      placeholder: 'Paste Link',
      validate: (str: string) =>
        str.length === 0 || str.startsWith('https://x.com/'),
      required: false,
    },
  });

  // todo carles: edit links
  // @TODO: LOCALIZE!!!
  // note: a form used to update the links of an already created token
  public updateLinksForm = new Form({
    telegramChannelLink: {
      title: 'Telegram channel link',
      placeholder: 'Paste Link',
      validate: (str: string) =>
        str.length === 0 || str.startsWith('https://t.me/'),
      required: false,
    },
    telegramChatLink: {
      title: 'Telegram chat link',
      placeholder: 'Paste Link',
      validate: (str: string) =>
        str.length === 0 || str.startsWith('https://t.me/joinchat/'),
      required: false,
    },
    twitterLink: {
      title: 'Twitter link',
      placeholder: 'Paste Link',
      validate: (str: string) =>
        str.length === 0 || str.startsWith('https://x.com/'),
      required: false,
    },
  });

  private offchainTokenRefresher?: NodeJS.Timeout;
  private offchainTokenRefreshCount = 0;

  private listingFetcher?: NodeJS.Timeout;

  private state = {
    loading: false,
    purchaseMode: 'buy' as TxType,
    searchTerm: '',
    extendSearch: false,
    selectedOffchainToken: undefined as Optional<TradingToken>,
    tx: undefined as Optional<Tx>,
    notifCount: -1, // -1 count as inactive notification
    notification: undefined as Optional<TradingSearchResult>,
    createOffchainTokenForm: undefined as Optional<
      TradingTokenInput & { id: string }
    >,
    listingCategory: DEFAULT_CATEGORY,
    pastNotificationIds: [] as string[],
    ownedOffchainTokensCache: [] as TradingSearchResult[],
    ownedOffchainTokensFetchTimestamp: 0,
    portfolioValue: -1,
  };

  constructor(private app: AppController) {
    super();
    app.addEventListener(AppEvents.onAppReady, this.init);
  }

  private init = async () => {
    /**
     * When we are showing the card page, start the refresh counter and whenever its up
     * refresh selected card data and restart counter
     */
    this.app.views.TradingTokenPage.onVisibilityChange((isShowing) => {
      clearInterval(this.offchainTokenRefresher);
      if (isShowing) {
        this.offchainTokenRefresher = setInterval(() => {
          if (this.offchainTokenRefreshCount === CARD_REFETCH_COOLDOWN_MS) {
            this.offchainTokenRefreshCount = 0;
            this.refreshSelectedOffchainToken();
          } else {
            this.offchainTokenRefreshCount++;
          }
          this.sendEvents(TradingEvents.OnRefreshTimeUpdate);
        }, SEC_IN_MS);
      } else {
        this.offchainTokenRefreshCount = 0;
      }
    });

    this.app.views.TradingPage.onVisibilityChange((isShowing) => {
      clearInterval(this.listingFetcher);
      if (isShowing) {
        this.state.selectedOffchainToken = undefined;
        let lastTopRefresh = Date.now();
        let lastNewRefresh = Date.now();
        this.listingFetcher = setInterval(() => {
          const now = Date.now();
          if (this.listingCategory === 'Hot') {
            this.refreshListing();
          } else if (this.listingCategory === 'Top') {
            if (now - lastTopRefresh > TOP_CATEGORY_REFRESH_COOLDOWN) {
              this.refreshListing();
              lastTopRefresh = now;
            }
          } else if (this.listingCategory === 'New') {
            if (now - lastNewRefresh > NEW_CATEGORY_REFRESH_COOLDOWN) {
              this.refreshListing();
              lastNewRefresh = now;
            }
          }
        }, LIST_FETCH_COOLDOWN);
      }
    });

    // pre-fetch the first page of memes
    this.searchOffchainTokens();

    if (getMyOffchainTokenIds(this.app.state).length > 0) {
      // avoid async call if no token
      // trigger a series of snapshots
      this.app.invoke.asyncTakePortfolioSnapshots();
    }

    // this.updateMyOffchainTokenStatuses();
  };

  private refreshListing = () => {
    this.searchOffchainTokens('isRefresh');
  };

  private refreshSelectedOffchainToken = async () => {
    if (!this.state.selectedOffchainToken) {
      return;
    }
    const offchainToken = await this.getOffchainToken(
      this.state.selectedOffchainToken.id,
    );
    if (offchainToken) {
      this.setSelectedOffchainToken(offchainToken, 'isRefresh');
    }
  };

  public goToTxConfirmation = async () => {
    if (!this.tx) {
      return;
    }

    await this.refreshSelectedOffchainToken();

    this.app.ui.drawer.show({
      id: 'drawerTradingTransactionConfirm',
      hideClose: false,
    });
  };

  public setSelectedOffchainToken = (
    offchainToken: TradingToken,
    isRefresh?: 'isRefresh',
  ) => {
    if (
      isRefresh ||
      offchainToken.id !== this.state.selectedOffchainToken?.id
    ) {
      this.state.selectedOffchainToken = offchainToken;
      const amount = isRefresh ? this.state.tx?.send : Big(0);
      this.updateTx(this.state.selectedOffchainToken, amount);
      this.sendEvents(TradingEvents.OnSelectedOffchainTokenUpdate);
    }
  };

  public setPurchaseMode = (mode: TxType) => {
    if (this.state.purchaseMode === mode) {
      return;
    }
    this.state.purchaseMode = mode;
    if (this.state.selectedOffchainToken) {
      this.updateTx(this.state.selectedOffchainToken);
      this.sendEvents(TradingEvents.OnTxUpdate);
    }
  };

  public setListingCategogy = (cat: TradingCategories) => {
    this.state.listingCategory = cat;
    if (!this.listingByCategory[this.listingCategory]) {
      this.searchOffchainTokens();
    }
  };

  public updateAmount = (value: Big) => {
    if (!this.state.tx) {
      return;
    }
    if (this.state.createOffchainTokenForm) {
      this.updateTx(
        { id: 'not_used_for_buy', supply: Big(0).toString() },
        value,
      );
      this.sendEvents(TradingEvents.OnTxUpdate);
    } else if (this.state.selectedOffchainToken) {
      this.updateTx(this.state.selectedOffchainToken, value);
      this.sendEvents(TradingEvents.OnTxUpdate);
    }
  };

  public createNewToken = async () => {
    this.app.track('memeoffchainToken_create_start');
    this.newTokenForm.clearForm();
    // start trading tutorial slideshow
    if (TRADING_TUTORIAL_ENABLED) {
      const starAmount = this.createStarPrice?.toString() ?? '0';
      await this.app.tutorial.startTutorial(
        Tutorials.SlideshowTradingCreateToken,
        {
          substitutes: { starAmount },
        },
      );
    }

    // open TradingCreatePage
    this.app.tutorial.step?.onAction &&
      this.app.tutorial.step?.onAction('trading-create');
    this.app.nav.goTo('TradingCreatePage');
  };

  // create final token form and open confirmation full-screen drawer
  public submitForm = async (retries = 0): Promise<void> => {
    if (!this.newTokenForm.isValid) {
      return;
    }

    const id = uuid();
    // Verify if Id is unique
    const existingOffchainTokens =
      await this.app.asyncGetters.getOffchainTokensFromOpenSearch({
        offchainTokenIds: [id],
      });

    if (existingOffchainTokens.length > 0) {
      retries++;
      if (retries > 3) {
        return this.app.ui.showError({
          message: `Something went wrong. Please try again`,
        });
      }
      return this.submitForm(retries);
    }

    this.state.createOffchainTokenForm = {
      ...this.newTokenForm.data,
      id,
    };
    this.updateTx({
      id: 'not_used_for_buy',
      supply: Big(0).toString(),
    });
    this.app.track(
      'memeoffchainToken_form_submit',
      this.state.createOffchainTokenForm,
    );
    this.app.ui.drawer.show({
      id: 'drawerTradingCreateConfirm',
      hideClose: true,
    });
  };

  // todo carles: update links
  public submitFormUpdateLinks = async (): Promise<void> => {
    if (!this.currentOffchainToken) {
      return;
    }

    await this.app.invoke.asyncEditOffchainToken({
      tokenId: this.currentOffchainToken.id,
      ...this.updateLinksForm.data,
    });

    this.refreshSelectedOffchainToken();

    // track update links analytics
    this.app.track('memecard_edit', {
      cardId: this.currentOffchainToken?.id || '',
      cardName: this.currentOffchainToken?.name || '',
    });

    // go back to original token page
    this.app.nav.back();
  };

  private getOffchainToken = async (offchainTokenId: string) => {
    const offchainToken = await this.app.asyncGetters.getOffchainToken({
      offchainTokenId,
    });

    if (!offchainToken) {
      return;
    }

    // the big int is serialized into a string
    const marketCap = offchainToken.overview.marketCap;
    if (typeof marketCap === 'string') {
      offchainToken.overview.marketCap = Big(marketCap);
    }

    return offchainToken;
  };

  private offchainTokenToListing = (
    offchainToken: TradingToken,
  ): TradingTokenListing => ({
    offchainTokenId: offchainToken.id,
    image: offchainToken.image,
    name: offchainToken.name,
    creatorId: offchainToken.creatorId,
    creatorName: offchainToken.creatorName,
    creatorImage: offchainToken.creatorImage || '',
    marketCap: offchainToken.overview.marketCap,
    ticker: offchainToken.ticker,
  });

  // @note: disabled as those methods were unused and were not safe with respect to Big numbers
  // getOffchainTokens = async (page: number = 1) => {
  //   // if (isLocal) {
  //   //   return this.offchainTokens;
  //   // }
  //   return await this.app.asyncGetters.listOffchainTokens({});
  // };

  // getOffchainTokenListing = async (): Promise<TradingTokenListing[]> => {
  //   const offchainTokens = await this.getOffchainTokens();
  //   return offchainTokens.map(this.offchainTokenToListing);
  // };

  private buyOffchainToken = async ({
    currencyAmount,
    tokenAmountEstimate,
  }: CanBuyOpts) => {
    if (this.app.playerBalance < currencyAmount.toNumber()) {
      return;
    }

    if (!this.state.selectedOffchainToken) {
      return;
    }

    this.app.ui.showSpinner();

    // Grab the current estimate and make sure we don't have some old ass estimate still
    let latestTokenAmountEstimate =
      await this.app.asyncGetters.getBuyOffchainTokenEstimate({
        offchainTokenId: this.state.selectedOffchainToken.id,
        currencyAmount,
      });

    if (
      latestTokenAmountEstimate &&
      !(latestTokenAmountEstimate instanceof Big)
    ) {
      latestTokenAmountEstimate = Big(latestTokenAmountEstimate);
    }

    const withinEstimate =
      latestTokenAmountEstimate &&
      getTxWithinEstimate(
        latestTokenAmountEstimate,
        tokenAmountEstimate,
        txConfig.clientSlippage,
      );

    if (!withinEstimate) {
      this.refreshSelectedOffchainToken();
      return this.app.ui.showError({
        message: `The estimate has changed, new estimate ${latestTokenAmountEstimate}`,
      });
    }

    const currentOwnedTokenAmount = Big(
      this.app.state.trading.offchainTokens[this.state.selectedOffchainToken.id]
        ?.tokenAmount || 0,
    );

    try {
      const response = await this.app.invoke.buyOffchainToken({
        offchainTokenId: this.state.selectedOffchainToken.id,
        currencyAmount: currencyAmount.toString(),
        tokenAmountEstimate: tokenAmountEstimate.toString(),
      });

      if (response.error) {
        // @TODO: Make this into a function
        if (response.code === ErrorCode.CUSTOM_SENTRY_TRACK) {
          captureGenericError(ErrorCode.CUSTOM_SENTRY_TRACK, response.data);
        }

        // If we are receiving a handled error most likely we have stale data, reload current item
        await this.refreshSelectedOffchainToken();

        throw new Error(response.error);
      } else if (response.data) {
        // Update selected offchainToken
        this.state.selectedOffchainToken = response.data;

        if (this.state.selectedOffchainToken) {
          const tokenAmount = Big(
            this.app.state.trading.offchainTokens[
              this.state.selectedOffchainToken.id
            ]!.tokenAmount,
          );
          const amount_bought = tokenAmount.minus(currentOwnedTokenAmount ?? 0);

          this.app.track('memeoffchainToken_buy', {
            offchainToken_name: this.state.selectedOffchainToken.name,
            offchainTokenID: this.state.selectedOffchainToken.id,
            previous_owned: currentOwnedTokenAmount.toString() ?? false,
            current_owned:
              this.app.state.trading.offchainTokens[
                this.state.selectedOffchainToken.id
              ]!.tokenAmount,
            amount_invested: currencyAmount.toString(),
            amount_bought: amount_bought.toString(),
            memeoffchainToken_price: currencyAmount
              .div(amount_bought)
              .toString(),
            total_holders: this.state.selectedOffchainToken.holderCount - 1, // exclude self
          });

          this.sendEvents(TradingEvents.OnSelectedOffchainTokenUpdate);
          this.app.invoke.asyncTakePortfolioSnapshots();
          this.refetchPortfolio('forceUpdate');
          this.app.ui.onOffchainTokenTxSuccess({
            offchainTokenId: this.state.selectedOffchainToken.id,
            offchainTokenImage: this.state.selectedOffchainToken.image,
            offchainTokenName: this.state.selectedOffchainToken.name,
            offchainTokenDescription:
              this.state.selectedOffchainToken.description.description,
            txAmount: amount_bought.toNumber(),
            txType: 'buy',
          });
        }
      }

      return response;
    } catch (e: any) {
      this.app.ui.showError({
        message: e.message,
      });
    }
  };

  private sellOffchainToken = async ({
    tokenAmount,
    currencyAmountEstimate,
  }: CanSellOpts) => {
    if (!this.state.selectedOffchainToken) {
      return;
    }

    const currentOwnedTokenAmount = Big(
      this.app.state.trading.offchainTokens[this.state.selectedOffchainToken.id]
        ?.tokenAmount,
    );

    // cai('sellOffchainToken', { offchainTokenId, price, amount });
    if (currentOwnedTokenAmount.lt(tokenAmount)) {
      return this.app.ui.showError({
        message: `Not enough tokens to sell`,
      });
    }

    this.app.ui.showSpinner();

    let latestCurrencyAmountEstimate =
      await this.app.asyncGetters.getSellOffchainTokenEstimate({
        offchainTokenId: this.state.selectedOffchainToken.id,
        tokenAmount,
      });

    if (!latestCurrencyAmountEstimate) {
      return this.app.ui.showError({
        message: `Could not estimate selling price`,
      });
    }

    if (typeof latestCurrencyAmountEstimate === 'string') {
      latestCurrencyAmountEstimate = Big(latestCurrencyAmountEstimate);
    }

    const withinEstimate = getTxWithinEstimate(
      latestCurrencyAmountEstimate,
      currencyAmountEstimate,
      txConfig.clientSlippage,
    );

    if (!withinEstimate) {
      this.refreshSelectedOffchainToken();
      return this.app.ui.showError({
        message: `The estimate has changed, new estimate ${latestCurrencyAmountEstimate}`,
      });
    }

    try {
      const response = await this.app.invoke.sellOffchainToken({
        offchainTokenId: this.state.selectedOffchainToken.id,
        tokenAmount: tokenAmount.toString(),
        currencyAmountEstimate: currencyAmountEstimate.toString(),
      });

      if (response.error) {
        // @TODO: Make this into a function
        if (response.code === ErrorCode.CUSTOM_SENTRY_TRACK) {
          captureGenericError(ErrorCode.CUSTOM_SENTRY_TRACK, response.data);
        }
        // If we are receiving a handled error most likely we have stale data, reload current item
        await this.refreshSelectedOffchainToken();

        throw new Error(response.error);
      } else if (response.data) {
        this.state.selectedOffchainToken = response.data.token;

        if (this.state.selectedOffchainToken) {
          const amount_divested = response.data.amount_divested;

          this.app.track('memeoffchainToken_sell', {
            offchainToken_name: this.state.selectedOffchainToken.name,
            offchainTokenID: this.state.selectedOffchainToken.id,
            previous_owned: currentOwnedTokenAmount.toString(),
            current_owned:
              this.app.state.trading.offchainTokens[
                this.state.selectedOffchainToken.id
              ]?.tokenAmount ?? 0,
            amount_divested,
            amount_sold: tokenAmount.toString(),
            memeoffchainToken_price: Big(amount_divested)
              .div(tokenAmount)
              .toString(),
            total_holders: this.state.selectedOffchainToken.holderCount - 1, // exclude self
          });
          this.sendEvents(TradingEvents.OnSelectedOffchainTokenUpdate);
          this.app.invoke.asyncTakePortfolioSnapshots();
          this.refetchPortfolio('forceUpdate');
          this.app.ui.onOffchainTokenTxSuccess({
            offchainTokenId: this.state.selectedOffchainToken.id,
            offchainTokenImage: this.state.selectedOffchainToken.image,
            offchainTokenName: this.state.selectedOffchainToken.name,
            offchainTokenDescription:
              this.state.selectedOffchainToken.description.description,
            txAmount: amount_divested,
            txType: 'sell',
          });
        }
      }
      return response;
    } catch (e: any) {
      this.app.ui.showError({
        message: e.message,
      });
    }
  };

  submitTransaction = () => {
    if (!this.state.selectedOffchainToken || !this.state.tx) {
      return;
    }

    const { send: amount, receive: price } = this.state.tx;
    let amountValue = amount;
    if (!(amount instanceof Big)) {
      amountValue = Big(amount);
    }

    if (this.isBuy) {
      return this.buyOffchainToken({
        currencyAmount: amountValue,
        tokenAmountEstimate: price,
        driftPct: txConfig.defaultMaxSlippage.toNumber(),
      });
    }

    return this.sellOffchainToken({
      tokenAmount: amountValue,
      currencyAmountEstimate: price,
      driftPct: txConfig.defaultMaxSlippage.toNumber(),
    });
  };

  waitForPurchaseConfirmation = async (
    productId: string,
    retries = 0,
  ): Promise<void> => {
    if (retries > 3) {
      throw new Error(`Purchase could not be confirmed. Try again later`);
    }
    const confirmed = await this.app.asyncGetters.getPurchaseConfirmed({
      productId,
    });
    if (confirmed) {
      return;
    }
    // How long to wait for? :thinking:
    await waitFor(1000);
    return this.waitForPurchaseConfirmation(productId, ++retries);
  };

  private createOffchainTokenLocally = async () => {
    if (!this.state.tx || !this.state.createOffchainTokenForm) {
      return;
    }

    try {
      const createOffchainTokenResponse =
        await this.app.invoke.asyncCreateOffchainToken({
          offchainToken: this.state.createOffchainTokenForm,
          currencyAmount: this.state.tx.send.toString(),
          productId: `foo`,
          isLocal: true,
          useCredit: false,
        });

      if (createOffchainTokenResponse.error) {
        return this.app.ui.showError({
          message: createOffchainTokenResponse.error,
        });
      }

      if (createOffchainTokenResponse.data) {
        const offchainToken = await this.getOffchainToken(
          createOffchainTokenResponse.data.offchainTokenId,
        );
        if (offchainToken) {
          this.app.invoke.asyncTakePortfolioSnapshots();

          // navigate back to pump page and show success drawer
          this.app.ui.onCreateOffchainTokenSuccess(offchainToken);
        }
      }
    } catch (e: any) {
      const isDupId = e.message.includes('duplicate state ID');
      if (isDupId) {
        this.app.nav.back();
        setTimeout(() => {
          this.app.ui.showError({
            message: `'Ticker' must be unique. Please try again`,
          });
        }, 350);
        return;
      }
      this.app.ui.showError({
        message: e.message,
      });
    } finally {
      this.state.createOffchainTokenForm = undefined;
    }
  };

  createOffchainToken = async () => {
    if (!this.state.tx || !this.state.createOffchainTokenForm) {
      return;
    }

    const existingOffchainToken =
      await this.app.asyncGetters.getOffchainTokensFromOpenSearch({
        offchainTokenIds: [this.state.createOffchainTokenForm.id],
      });

    if (existingOffchainToken.length > 0) {
      return this.app.ui.showError({
        message: `'Ticker' must be unique. Please try again`,
      });
    }

    this.app.ui.showSpinner();

    if (isLocal) {
      return this.createOffchainTokenLocally();
    }
    this.app.track('memeoffchainToken_init_creation_purchase', {
      memeoffchainToken_name: this.state.createOffchainTokenForm.name,
      creator_invested_amount: this.state.tx.send.toString(),
    });

    try {
      const iapConfig = this.app.session.getIAPConfig();

      if (!iapConfig) {
        throw new Error(
          `User trying to createOffchainToken without 'iapConfig'.`,
        );
      }

      const useCredit = this.app.state.tokenCreationCredits > 0;

      const iapLink = await this.app.asyncGetters.getInvoiceLink({
        ...iapConfig,
        analytics: {
          feature: 'memeoffchainToken',
          subFeature: 'memeoffchainToken_creation',
        },
      });

      const iapResponse = useCredit ? 'success' : await this.buyIAP(iapLink);
      // If the user approves the transaction in telegram ui
      if (iapResponse === 'success') {
        if (!useCredit) {
          await this.waitForPurchaseConfirmation(iapConfig.productId);
        }

        // get all properties except image
        const { image: _, ...rest } = this.state.createOffchainTokenForm;

        const createOffchainTokenResponse =
          await this.app.invoke.asyncCreateOffchainToken({
            offchainToken: { image: '', ...rest },
            currencyAmount: this.state.tx.send.toString(),
            productId: iapConfig.productId,
            isLocal: false,
            useCredit,
          });

        if (createOffchainTokenResponse.error) {
          return this.app.ui.showError({
            message: createOffchainTokenResponse.error,
          });
        }

        await this.app.replicant.uploadUserAsset(
          this.state.createOffchainTokenForm.image,
          `${S3_PATH_PREFIX}/${OFFCHAIN_TRADING_PREFIX}-${this.state.createOffchainTokenForm.id}/`,
        );

        // const tokenImageUrl = this.app.replicant.getUserAssetUrl(image);
        const offchainTokenId =
          createOffchainTokenResponse.data.offchainTokenId;

        if (offchainTokenId) {
          this.app.track('memecard_created', {
            memecard_name: this.state.createOffchainTokenForm.name,
            cardID: offchainTokenId,
            owner_amount_invested: this.state.tx.send.toString(),
          });

          const offchainToken = await this.getOffchainToken(offchainTokenId);
          this.app.ui.onCreateOffchainTokenSuccess(offchainToken);

          this.state.createOffchainTokenForm = undefined;
          this.newTokenForm.clearForm();
        } else {
          throw new Error(`Failed to create offchainToken`);
        }
      }
    } catch (e: any) {
      const isDupId = e.message.includes('duplicate state ID');
      if (isDupId) {
        setTimeout(() => {
          this.app.ui.showError({
            message: `'Ticker' must be unique. Please try again`,
          });
        }, 350);
        return;
      }
      this.app.ui.showError({
        message: e.message,
      });
    } finally {
      // Do we want to wipe the data when it fails?
    }
  };

  refreshMyOffchainTokens = async (maxCacheDuration: number = 0) => {
    if (this.state.ownedOffchainTokensCache) {
      if (
        this.state.ownedOffchainTokensFetchTimestamp + maxCacheDuration >=
        Date.now()
      ) {
        return this.state.ownedOffchainTokensCache;
      }
    }

    const myOffchainTokenIds = getMyOffchainTokenIds(this.app.state);
    const allMyOffchainTokensFromSearch: TradingSearchResult[] =
      await this.app.asyncGetters.getOffchainTokensFromOpenSearch({
        offchainTokenIds: myOffchainTokenIds,
      });

    this.state.ownedOffchainTokensCache = allMyOffchainTokensFromSearch.filter(
      (offchainTokenSearchResult) =>
        offchainTokenSearchResult.status !== 'deleted',
    );

    return this.state.ownedOffchainTokensCache;
  };

  getMyOffchainTokensListing = async () => {
    const offchainTokenListings = (await this.refreshMyOffchainTokens())
      // .sort((a, b) => a[searchField] - b[searchField])
      .map((offchainTokenSearchResult) =>
        getOffchainTokenListing(offchainTokenSearchResult),
      );

    const myTokens = this.app.state.trading.offchainTokens;
    const tokensOrder = Object.keys(myTokens).sort((a, b) =>
      Big(myTokens[b].currencyInvested)
        .minus(myTokens[a].currencyInvested)
        .toNumber(),
    );

    return offchainTokenListings.sort(
      (a, b) =>
        tokensOrder.indexOf(a.offchainTokenId) -
        tokensOrder.indexOf(b.offchainTokenId),
    );
  };

  // @TODO: Probably best somewhere else more 'generic'
  private buyIAP = (iapLink: string): Promise<'success' | 'cancel'> => {
    return new Promise((resolve, reject) => {
      // Seems like the type is wrong, first parameter of callback is status
      Telegram.WebApp.openInvoice(iapLink, (status) => {
        switch (status) {
          case 'paid':
            return resolve('success');
          case 'cancelled':
            return resolve('cancel');
          case 'failed':
            return reject();
        }
      });
    });
  };

  getHolders = async (): Promise<TradingTokenHolder[]> => {
    if (this.currentOffchainToken === undefined) {
      return [];
    }

    const currentOffchainToken = this.currentOffchainToken;

    const holders = await this.app.asyncGetters.getOffchainTokenHolders({
      offchainTokenIds: [currentOffchainToken.id],
    });

    // using player state to display player's holder status in tab,
    // this fixes update
    const userHolder: TradingTokenHolder[] = [];
    const offchainTokens = this.app.state.trading.offchainTokens;
    const ownedToken = offchainTokens[currentOffchainToken.id];
    if (ownedToken && Big(ownedToken.tokenAmount).gt(0)) {
      userHolder.push({
        userId: currentOffchainToken.creatorId,
        offchainTokenCount: Big(ownedToken.tokenAmount),
        offchainTokenMarketShare: getPct(
          Big(ownedToken.tokenAmount).toNumber(),
          Big(currentOffchainToken.supply).toNumber(),
        ),
        image: this.app.state.profile.photo,
        name: this.app.state.profile.name,
        type: Boolean(ownedToken.productId) ? 'creator' : 'normal',
      });
    }

    const otherTokenHolders = holders
      .filter((holder) => {
        return holder.id !== this.app.state.id;
      })
      .map((holder) => {
        const offchainTokens = holder.offchainTokens;
        const offchainToken = offchainTokens.find(
          (token) => token?.id === currentOffchainToken.id,
        )!;

        const tokenAmount = offchainToken.tokenAmount ?? 0;

        return {
          userId: holder.id,
          offchainTokenCount: Big(tokenAmount),
          offchainTokenMarketShare: getPct(
            Big(tokenAmount).toNumber(),
            Big(currentOffchainToken.supply).toNumber(),
          ),
          image: holder.photo,
          name: holder.name,
          type: offchainToken.isCreator ? 'creator' : 'normal',
        };
      }) as TradingTokenHolder[];

    // @todo: enable if sorting by holdings in the search doesnt work
    // otherTokenHolders.sort((a, b) => {
    //   return b.offchainTokenCount.minus(a.offchainTokenCount).toNumber();
    // });

    return userHolder.concat(otherTokenHolders);
  };

  setSearchTerm = (searchTerm: string) => {
    this.state.searchTerm = searchTerm;
    this.refreshListing();
  };

  setExtendSearch = (extendSearch: boolean) => {
    this.state.extendSearch = extendSearch;
  };

  private updateListing = (value: TradingTokenListingCache) => {
    this.listingByCategory[this.listingCategory] = value;
  };

  private requestListing = async (
    updateListingFn: (
      newListing: TradingTokenListing[],
    ) => TradingTokenListing[],
    isRefresh = false,
  ) => {
    if (this.listingCategory === 'MyCards') {
      this.updateListing({
        listing: await this.getMyOffchainTokensListing(),
        lastOffchainTokenSortFieldValue: -1,
        lastOffchainTokenAvailabilityTime: -1,
        isEndOfList: true,
      });
      this.sendEvents(TradingEvents.OnListingUpdate);
      return;
    }

    const searchField = searchFields[this.listingCategory];

    const offchainTokenSearch = this.listingByCategory[this.listingCategory];

    const props = {
      searchString: this.state.searchTerm,
      field: searchField,
      // undefined gets the first page, refresh just refreshes the first page
      lessThan: isRefresh
        ? undefined
        : offchainTokenSearch?.lastOffchainTokenSortFieldValue,
      availableAfterIfEqual: isRefresh
        ? undefined
        : offchainTokenSearch?.lastOffchainTokenAvailabilityTime,
    };

    const offchainTokens = await this.app.asyncGetters.searchOffchainTokens(
      props,
    );

    // @CAI
    const {
      lastOffchainTokenSortFieldValue,
      lastOffchainTokenAvailabilityTime,
    } = getNextPaginator({
      offchainTokens,
      searchField,
      currentPaginator: offchainTokenSearch,
      isRefresh,
    });

    const offchainTokenListings = offchainTokens
      // .sort((a, b) => a[searchField] - b[searchField])
      .map((offchainTokenSearchResult) =>
        getOffchainTokenListing(offchainTokenSearchResult),
      );

    let listing: TradingTokenListing[] = [];

    if (offchainTokenSearch) {
      listing = updateListingFn(offchainTokenListings);
    } else {
      listing = offchainTokenListings;
    }

    const newOffchainTokenResults = {
      listing,
      lastOffchainTokenSortFieldValue,
      lastOffchainTokenAvailabilityTime,
      isEndOfList: lastOffchainTokenSortFieldValue === -1,
    };
    this.updateListing(newOffchainTokenResults);
    this.setExtendSearch(false);
    this.sendEvents(TradingEvents.OnListingUpdate);
  };

  paginate = async () => {
    const offchainTokenSearch = this.listingByCategory[this.listingCategory];
    // To paginate the initial fetch has to be done, so it can't be undefined
    if (!offchainTokenSearch) {
      return;
    }
    this.requestListing((offchainTokenListings: TradingTokenListing[]) => {
      let listing: TradingTokenListing[] = [];

      const updateIds: string[] = [];

      offchainTokenSearch.listing.forEach((cachedListing) => {
        const updateListing = offchainTokenListings.find(
          (l) => l.offchainTokenId === cachedListing.offchainTokenId,
        );
        if (updateListing) {
          updateIds.push(cachedListing.offchainTokenId);
          listing.push(updateListing);
        } else {
          listing.push(cachedListing);
        }
      });

      listing = [
        ...listing,
        // Add the new items by removing the ones that were updates
        ...offchainTokenListings.filter(
          (l) => !updateIds.includes(l.offchainTokenId),
        ),
      ];

      return listing;
    });
  };

  /**
   * Use this to get or update the top offchainTokens for the selected tab
   * @returns
   */
  searchOffchainTokens = async (
    isRefresh: Optional<'isRefresh'> = undefined,
  ) => {
    const offchainTokenSearch = this.listingByCategory[this.listingCategory];

    this.requestListing((offchainTokenListings: TradingTokenListing[]) => {
      if (!offchainTokenSearch) {
        return [];
      }
      let listing: TradingTokenListing[] = [];

      const [, , , , , , , , , , ...index10onwards] =
        offchainTokenSearch.listing;

      listing = [...offchainTokenListings, ...index10onwards];

      return listing;
    }, isRefresh === 'isRefresh');
  };

  /**
   *
   * @returns Integer
   */
  getTxFee = () => {
    if (!this.state.tx) {
      return -1;
    }

    const { txType, send, receive } = this.state.tx;
    const price = txType === 'buy' ? send : receive;

    const basePrice = price.div(1 + txConfig.fee);
    const txFee = Math.ceil(price.minus(basePrice).toNumber());

    return txFee;
  };

  private updateTx = (
    selectedOffchainToken: Pick<TradingToken, 'supply' | 'id'>,
    amount?: Big,
  ) => {
    if (this.purchaseMode === 'buy') {
      const buyTx = {
        currencyAmount: amount ?? Big(0),
        currentSupply: Big(selectedOffchainToken.supply),
      };

      this.state.tx = {
        txType: this.purchaseMode,
        receive: getBuyTxEstimate(buyTx),
        send: buyTx.currencyAmount,
        isValid: getIsBuyTxValid(this.app.state, buyTx),
      };
    } else {
      const sellTx = {
        tokenAmount: amount ?? Big(0),
        currentSupply: Big(selectedOffchainToken.supply),
      };

      this.state.tx = {
        txType: this.purchaseMode,
        receive: getCoinAmountForTokenSell(
          sellTx.currentSupply,
          sellTx.tokenAmount,
        ),
        send: sellTx.tokenAmount,
        isValid: getIsSellTxValid(
          this.app.state,
          selectedOffchainToken.id,
          sellTx,
        ),
      };
    }
  };

  private getNotification = async () => {
    // alternating between new offchainTokens and last transaction
    const field =
      this.state.notifCount % 2 === 0 ? 'lastTx.createdAt' : 'availableAt';
    this.state.notifCount += 1;

    const notification = await this.app.asyncGetters.getHotOffchainToken({
      field,
      unwantedTokenIds: this.state.pastNotificationIds,
    });

    if (this.notifScheduler === null) {
      // was deactivated during the fetch
      return;
    }

    this.state.notification = notification;

    if (notification !== undefined) {
      this.state.pastNotificationIds.push(notification.id);

      // The notification will loop through at least 16 notifications
      // in theory updates to cards should happen so frequently that loops wont happen
      // if not enough items exists, then notifications do not even loop
      const minNotifLoop = 16;
      if (this.state.pastNotificationIds.length > minNotifLoop) {
        this.state.pastNotificationIds = this.state.pastNotificationIds.slice(
          this.state.pastNotificationIds.length - minNotifLoop,
        );
      }
    }

    this.sendEvents(TradingEvents.OnNotification);

    const timeVariation = 1.0;
    const delay =
      offchainTradingNotifDelay *
      (1 - timeVariation / 2 + timeVariation * Math.random());
    this.notifScheduler = setTimeout(() => this.getNotification(), delay);
  };

  private scheduleNotifications = () => {
    this.state.notifCount = 0;
    this.notifScheduler = setTimeout(
      () => this.getNotification(),
      offchainTradingNotifDelay,
    );
  };

  setNotificationActivity = (active: boolean) => {
    if (active) {
      if (this.notifScheduler !== null) {
        // already active
        return;
      }

      this.scheduleNotifications();
      return;
    }

    if (this.notifScheduler !== null) {
      // active, deactivate
      this.state.notifCount = -1;
      clearTimeout(this.notifScheduler);
      this.notifScheduler = null;
    }
  };

  setOffchainToken = async (offchainTokenId: string) => {
    // fetch offchainToken data
    const offchainToken = await this.getOffchainToken(offchainTokenId);
    if (offchainToken) {
      // select the offchainToken
      this.setSelectedOffchainToken(offchainToken);
      // open TradingTokenPage to render offchainToken details
      return offchainTokenId;
    }
    return undefined;
  };

  setMaxSlippage = (value: number) => {
    this.app.invoke.setMaxSlippage({ value });
    this.sendEvents(TradingEvents.OnTxUpdate);
  };

  getOffchainTokenPricePoints = (
    offchainToken: TradingToken,
    sliceConfig: SliceConfig | null,
  ) => {
    return getOffchainTokenPricePoints(offchainToken, sliceConfig);
  };

  getPortfolioPricePoints = async (sliceConfig: SliceConfig | null) => {
    await this.refreshMyOffchainTokens(PORTFOLIO_REFETCH_COOLDOWN);

    return getPortfolioPricePoints(
      this.app.state,
      sliceConfig,
      this.state.ownedOffchainTokensCache,
    );
  };

  getOffchainTokenHoldCount = () => {
    return getOffchainTokenHoldCount(this.app.state);
  };

  getPortfolioRoi = () => {
    return getPortfolioRoi(this.app.state, this.portfolioValue);
  };

  getOffchainTokenHoldingStats = (
    offchainTokenId: string,
    tokenHolding: OwnedOffchainToken,
  ) => {
    return getOffchainTokenHoldingStats(
      offchainTokenId,
      this.state.ownedOffchainTokensCache,
      tokenHolding,
    );
  };

  refetchPortfolio = async (
    forceUpdate: Optional<'forceUpdate'> = undefined,
  ) => {
    await this.refreshMyOffchainTokens(
      forceUpdate ? 0 : PORTFOLIO_REFETCH_COOLDOWN,
    );

    const portfolioValueBefore = this.state.portfolioValue;
    const portfolioValue = computePortfolioValue(
      this.app.state,
      this.state.ownedOffchainTokensCache,
    ).toNumber();

    const hasChanged = portfolioValueBefore !== portfolioValue;

    this.state.portfolioValue = portfolioValue;

    if (hasChanged) {
      this.sendEvents(TradingEvents.OnPortfolioUpdate);
    }
  };

  shareOffchainToken = async (props: TransactionSuccess | undefined) => {
    if (!props) {
      return;
    }

    const myToken =
      this.app.state.trading.offchainTokens[props.offchainTokenId];

    const tokenBalace = myToken ? Big(myToken.tokenAmount).toNumber() : 0;

    this.app.track('memecard_share', {
      memecard_name: props.offchainTokenName,
      token_balance: tokenBalace,
      cardID: props.offchainTokenId,
      action: props.mode,
    });

    await analytics.flush();
    // leave time to flush (just in case)
    await delay(500);

    shareDeeplink('TradingTokenPage', {
      messageOpts: {
        title: props?.offchainTokenName as unknown as string,
        text: props?.offchainTokenDescription as unknown as string,
      },
      deeplinkOpts: this.app.nav.getDeeplinkOpts('TradingTokenPage'),
    });
  };
}
